import { useEffect, useState } from "react";
import {
    Download, ShieldCheck, ScanLine, ScanText, Tags, FileEdit, Database,
    GitCompare, Recycle, LayoutDashboard, Rocket, BookOpen, TrendingUp,
    ArrowDown, CheckCircle2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const STAGES = [
    { key: "capture",              name: "Capture",                icon: Download,        color: "#D4AF37" },
    { key: "validate",             name: "Validate",               icon: ShieldCheck,     color: "#3B82F6" },
    { key: "digitise",             name: "Digitise",               icon: ScanLine,        color: "#F59E0B" },
    { key: "ocr",                  name: "OCR",                    icon: ScanText,        color: "#10B981" },
    { key: "classification",       name: "AI Classification",      icon: Tags,            color: "#8B5CF6" },
    { key: "metadata",             name: "Metadata Enrichment",    icon: FileEdit,        color: "#22D3EE" },
    { key: "repository",           name: "Knowledge Repository",   icon: Database,        color: "#D4AF37" },
    { key: "similarity",           name: "Similarity Analysis",    icon: GitCompare,      color: "#3B82F6" },
    { key: "reuse",                name: "Engineering Reuse",      icon: Recycle,         color: "#10B981" },
    { key: "exec_intel",           name: "Executive Intelligence", icon: LayoutDashboard, color: "#EAB308" },
    { key: "future_reuse",         name: "Future Project Reuse",   icon: Rocket,          color: "#F59E0B" },
    { key: "lessons",              name: "Lessons Learned",        icon: BookOpen,        color: "#22D3EE" },
    { key: "growth",               name: "Continuous Growth",      icon: TrendingUp,      color: "#D4AF37" },
];

export default function KnowledgeLifecycle() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/edi/lifecycle")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load lifecycle"));
    }, []);

    const throughput = data?.throughput || {};

    return (
        <div className="glass rounded-xl p-6" data-testid={EDI.lifecycleRoot}>
            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                <span className="kicker text-[10px]">Enterprise Knowledge Lifecycle™</span>
            </div>
            <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">
                Capture Once. Use Many Times.™
            </h3>
            <p className="mt-1 text-slate-400 text-sm font-body max-w-2xl">
                Every engineering document flows through this thirteen-stage pipeline — from initial
                capture all the way to continuous corporate knowledge growth.
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {STAGES.map((s, i) => {
                    const Icon = s.icon;
                    const count = throughput[s.key] ?? "—";
                    return (
                        <div
                            key={s.key}
                            data-testid={EDI.lifecycleStage(s.key)}
                            className="relative rounded-md border border-slate-800 bg-slate-950/50 p-4 hover:border-[color:var(--sgsb-gold)]/40 transition-colors duration-200"
                        >
                            <div className="absolute top-0 left-0 h-[2px] w-8" style={{ background: s.color }} />
                            <div className="flex items-center gap-3">
                                <div
                                    className="h-9 w-9 rounded-md grid place-items-center border shrink-0"
                                    style={{
                                        borderColor: `${s.color}55`,
                                        background: `${s.color}14`,
                                    }}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={1.5} style={{ color: s.color }} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="kicker text-slate-500 text-[10px]">Stage {i + 1}</div>
                                        <CheckCircle2 className="h-3 w-3 text-[var(--sgsb-gold)]" strokeWidth={2} />
                                    </div>
                                    <div className="font-display font-black text-white text-sm tracking-tight leading-tight">
                                        {s.name}
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-body mt-0.5">
                                        {typeof count === "number" ? count.toLocaleString() : count} items
                                    </div>
                                </div>
                            </div>
                            {i < STAGES.length - 1 && (
                                <ArrowDown
                                    className="hidden xl:block absolute -right-3 top-1/2 -translate-y-1/2 rotate-[-90deg] h-4 w-4 text-slate-700 z-10"
                                    strokeWidth={1.5}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    ["End-to-End Cycle", "14 d"],
                    ["Items In-Flight", "3,240"],
                    ["Automation Coverage", "87%"],
                    ["SLA Compliance", "98%"],
                ].map(([k, v]) => (
                    <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                        <div className="kicker text-slate-500 text-[10px]">{k}</div>
                        <div className="mt-1 font-display font-black text-white text-lg tracking-tight">{v}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
